import { SearchMd } from '@untitledui/icons'
import { type ChangeEvent, useState } from 'react'

import { Popover, PopoverContent } from '@components/designSystem/Popover'
import ToolbarButton from '@components/toolbar/ToolbarButton'
import useTypedTranslation from '@hooks/useTypedTranslation'
import { useLayout } from '@state/layout/queries'
import { cx } from '@utils/cx'

interface ShelfSearchProps {
  selectedShelfId?: string
  onShelfIdSelect: (shelfId?: string) => void
}

function ShelfSearch({ selectedShelfId, onShelfIdSelect }: ShelfSearchProps) {
  const listId = 'shelf-search-list'
  const [isActive, setActive] = useState(false)
  const { t } = useTypedTranslation()
  const { data: layout } = useLayout()
  const shelves = layout?.floors[0]?.shelves
  const shelvesWithExternalId = (shelves ?? [])
    .filter(shelf => shelf.name)
    .sort((a, b) => a.name.localeCompare(b.name))

  const [userInput, setUserInput] = useState('')
  const [noMatch, setNoMatch] = useState(false)

  const selectedShelf = shelves?.find(shelf => shelf.id === selectedShelfId)
  const derivedInput = selectedShelf?.name ?? ''

  // Use derived input when no user input, otherwise use user input
  const input = userInput || derivedInput

  function onChange(ev: ChangeEvent<HTMLInputElement>) {
    setUserInput(ev.target.value)
    const lcValue = ev.target.value.toLowerCase()
    const matches = shelvesWithExternalId.filter(shelf => shelf.name.toLowerCase().includes(lcValue))

    // we've found an exact matching shelf id
    if (matches.length === 1 && matches[0]!.name === ev.target.value) {
      setNoMatch(false)
      onShelfIdSelect(matches[0]!.id)
      setActive(false)
      return
    }

    // we've found at least one shelf id that could match
    if (matches.length >= 1) {
      setNoMatch(false)
      onShelfIdSelect(undefined)
      return
    }

    // we've found no matching shelf ids
    setNoMatch(true)
    onShelfIdSelect(undefined)
    return
  }

  const hasExternalShelfIds = shelvesWithExternalId.length > 0

  return (
    <Popover isOpen={isActive} onOpenChange={setActive}>
      <ToolbarButton label={t('storeMap.shelfSearch.tooltip')} isActive={isActive} shouldDisableTooltipOnActive={true} data-testid="shelf-search-btn">
        <SearchMd/>
      </ToolbarButton>
      <PopoverContent placement='left' isNonModal={true}>
        {hasExternalShelfIds ?
          <>
            <input
              placeholder={t('storeMap.shelfSearch.placeholder')}
              list={listId}
              value={input}
              className={cx('rounded-sm border', noMatch ? 'border-border-error' : 'border-border-secondary')}
              onChange={onChange}
            />
            <datalist id={listId}>
              {shelvesWithExternalId.map(({ id, name }) => <option key={id} value={name} />)}
            </datalist>
            <p className='h-5 text-sm text-text-error-primary'>{noMatch ? t('storeMap.shelfSearch.noMatch') : ''}</p>
          </>
          :
          <p className='text-sm text-text-secondary'>{t('storeMap.shelfSearch.noIds')}</p>
        }
      </PopoverContent>
    </Popover>
  )
}

export default ShelfSearch